
import Vue from 'vue';
import Component from 'vue-class-component';
import { BoPanel } from 'bonomo';
import CreateButton from '@/components/shared/CreateButton.vue';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import { apiKeysService, IApiKey, IApiKeyEndpoint } from '@/services/apiKeysService';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { DateHelper } from '@/helpers/DateHelper';

@Component({
  name: 'ApiKeys',
  components: { BoPanel, CreateButton, Datim, PaginationBar, SimpleConfirmationDialog },
})
export default class ApiKeys extends Vue {
  protected loading: boolean = true;
  protected apiKeys: IApiKey[] = [];
  protected currentPage: number = 1;
  protected lastPage: number = 1;
  protected total: number = 0;
  protected selectedPerPage: number = 15;
  readonly perPageOptions: number[] = [15, 25, 50];

  protected endpointsCatalog: IApiKeyEndpoint[] = [];

  protected createDialog: boolean = false;
  protected createFormValid: boolean = false;
  protected creating: boolean = false;
  protected createError: boolean = false;
  protected newName: string = '';
  protected newEndpointIds: number[] = [];

  protected revealDialog: boolean = false;
  protected revealedKey: string = '';

  protected guideDialog: boolean = false;

  protected showRevokeDialog: boolean = false;
  protected apiKeyToRevoke: IApiKey | null = null;
  protected revoking: number | null = null;

  protected snackbar: boolean = false;
  protected snackbarText: string = '';

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('apiKeys.fields.name') as string, value: 'name', sortable: false },
      { text: this.$t('apiKeys.fields.status') as string, value: 'status', sortable: false },
      { text: this.$t('apiKeys.fields.endpoints') as string, value: 'endpoint_ids', sortable: false },
      { text: this.$t('common.createdAt') as string, value: 'created_at', sortable: false },
      { text: this.$t('apiKeys.fields.lastUsedAt') as string, value: 'last_used_at', sortable: false },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ];
  }

  async loadData() {
    await Promise.all([
      this.fetchPage(this.currentPage),
      this.fetchEndpointsCatalog(),
    ]);
  }

  async fetchEndpointsCatalog() {
    this.endpointsCatalog = await apiKeysService.fetchEndpointsCatalog();
  }

  async fetchPage(page: number) {
    this.loading = true;
    try {
      const result = await apiKeysService.fetchAll(page, this.selectedPerPage);
      this.apiKeys = result.data;
      this.currentPage = result.current_page;
      this.lastPage = Math.max(1, Math.ceil(result.total / (result.per_page || this.selectedPerPage)));
      this.total = result.total;
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number) {
    this.fetchPage(page);
  }

  onPerPageChange(perPage: number) {
    this.selectedPerPage = perPage;
    this.fetchPage(1);
  }

  openCreateDialog() {
    this.newName = '';
    this.newEndpointIds = [];
    this.createError = false;
    this.createDialog = true;
  }

  async submitCreate() {
    if (!this.newName) return;
    this.creating = true;
    this.createError = false;
    try {
      const created = await apiKeysService.create(this.newName, this.newEndpointIds);
      this.createDialog = false;
      this.revealedKey = created.plain_key;
      this.revealDialog = true;
      await this.fetchPage(1);
    } catch (_e) {
      this.createError = true;
    } finally {
      this.creating = false;
    }
  }

  copyRevealedKey() {
    navigator.clipboard.writeText(this.revealedKey).then(() => {
      this.showSnackbar(this.$t('apiKeys.revealModal.copied') as string);
    });
  }

  closeRevealDialog() {
    this.revealDialog = false;
    this.revealedKey = '';
  }

  lastUsedLabel(item: IApiKey): string {
    return DateHelper.relativeTimeFormat(
      item.last_used_at as string,
      this.$t('common.today') as string,
      this.$t('common.yesterday') as string,
    );
  }

  longLastUsed(item: IApiKey): string {
    return DateHelper.longDateTimeFormat(item.last_used_at as string);
  }

  endpointNames(item: IApiKey): string {
    const names = this.endpointsCatalog
      .filter((endpoint) => item.endpoint_ids.includes(endpoint.id))
      .map((endpoint) => endpoint.name);
    return names.length ? names.join(', ') : (this.$t('apiKeys.fields.noEndpoints') as string);
  }

  askRevoke(item: IApiKey) {
    this.apiKeyToRevoke = item;
    this.showRevokeDialog = true;
  }

  async confirmRevoke() {
    if (!this.apiKeyToRevoke) return;
    const id = this.apiKeyToRevoke.id;
    this.showRevokeDialog = false;
    this.revoking = id;
    try {
      await apiKeysService.revoke(id);
      await this.fetchPage(this.currentPage);
    } catch (_e) {
      this.showSnackbar(this.$t('common.errorHappened') as string);
    } finally {
      this.revoking = null;
      this.apiKeyToRevoke = null;
    }
  }

  private showSnackbar(text: string) {
    this.snackbarText = text;
    this.snackbar = true;
  }
}
